import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useSessionStore, labelMap } from './SessionStore';
import { useSiteSettings } from './context/SiteSettingsContext';
import ModalWrapper from './components/Modal/ModalWrapper';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LabelIcon from '@mui/icons-material/Label';
import MicIcon from '@mui/icons-material/Mic';

import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ArticleIcon from '@mui/icons-material/Article';
import Portal from './components/Portal';
import { useDialog } from './components/Modal/DialogContext';
import { Link } from "react-router-dom"; // Stelle sicher, dass NavLink importiert ist
import type { AppMode } from './App';

interface WakeLockSentinel {
  release: () => Promise<void>;
}

interface WakeLock {
  request: (type: 'screen') => Promise<WakeLockSentinel>;
}

interface AudioRecorderProps {
  isAudioDialogOpen: boolean; // Zustand, ob der Dialog geöffnet ist
  setIsAudioDialogOpen: React.Dispatch<React.SetStateAction<boolean>>; // Funktion zum Ändern des Dialogzustands
}

interface DictateTabProps {
  mode: AppMode;
}

export const generateAudioTimestamp = (extension: string = ""): string => {

  return `audio_${new Date().toISOString().replace(/[:.]/g, '-')}${extension}`;
};

export const triggerUploadCompletion = async (session_id: string, filename: string, audioname: string, lastchunkid: number) => {


  try {
    console.log({
      filename,
      audioname,
      lastchunkid,
    });
    const response = await axios.post(`/api/services/complete-upload/`, {
      session_id: session_id, filename: filename, audioname: audioname, lastchunkid: lastchunkid
    });

    console.log('Audio-Processing abgeschlossen:', response.data);
    //alert(`Audio-Datei wurde erfolgreich verarbeitet: ${response.data.final_file}`);
  } catch (error) {
    console.error('Fehler beim Abschließen der Audioverarbeitung:', error);
    alert('Fehler beim Abschließen der Verarbeitung. Details in der Konsole.');
  }
};

const DictateTab: React.FC<DictateTabProps> = ({ mode }) => {
  const isCase = mode === "case";
  void mode;
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const { fetchSessionDataById, setSelectedSessionId, selectedSessionId, setIsSessionSelected } = useSessionStore();
  const dialog = useDialog();


  const [showTipsDialog, setShowTipsDialog] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingState, setRecordingState] = useState<string>('inactive'); // Mögliche Werte: 'inactive', 'recording', 'paused'
  const [recordingTime, setRecordingTime] = useState(0); // in Sekunden
  const [audioName, setAudioName] = useState<string>('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recoderSessionId, setRecoderSessionId] = useState<string>('');
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [generatedFilename, setGeneratedFilename] = useState<string>("empty");
  const sessionIdRef = useRef<string>(''); // Ref für session_id
  const chunkIdRef = useRef<number>(0); // ID des Chunks
  const fileNameRef = useRef<string>(''); // ID des Chunks
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStateRef = useRef<string>('inactive');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  // Für den Autio-Level-Leser
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const volumeBarRef = useRef<HTMLCanvasElement | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);


  const timerRef = useRef<NodeJS.Timeout | null>(null);


  // Für die Audioaufnahem
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (recordingState === 'recording') {
      startVolumeMonitoring();
    }
  }, [recordingState]);

  useEffect(() => {
    return () => {
      setRecordingState('inactive'); // Stoppe die Loop eindeutig!

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);


  // Audio streaming
  // For timer
  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };


  const sendHearbeat = async () => {
    try {
      // Optional: Start-Logik für Aufnahme hier

      // 🔄 Heartbeat senden
      const res = await axios.get('/auth/api/heartbeat/');
      if (!isSiteSettingsLoading && logLevel >= 3) console.log("Heartbeat gesendet:", res.data);

      // Optional: Weiter mit Aufnahme-Logik
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Heartbeat fehlgeschlagen:", error);
    }
  };


  const startRecording = async () => {
    sendHearbeat(); // Heartbeat senden
    setRecordingTime(0); // Zeit zurücksetzen
    if (timerRef.current) clearInterval(timerRef.current); // Timer zurücksetzen
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);

    // generate session_id and file name with timestamp
    sessionIdRef.current = generateAudioTimestamp();
    setRecoderSessionId(sessionIdRef.current)

    fileNameRef.current = `${sessionIdRef.current}.wav`;
    setGeneratedFilename(fileNameRef.current)

    chunkIdRef.current = 0; // Initialisieren der chunk_id
    const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    setStream(audioStream);
    audioStreamRef.current = audioStream;
    recordingStateRef.current = 'recording';

    startRecordingLoop()
  }



  const startRecordingLoop = async () => {
    try {
      audioChunksRef.current = [];

      // Fallback für Safari: prüfe unterstützte MIME-Typen
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        // Safari: fallback auf unkomprimiertes WAV (wenn unterstützt)
        mimeType = 'audio/wav';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          alert('Dieser Browser unterstützt kein geeignetes Audioformat für die Aufnahme.');
          return;
        }
      }

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256; // Anzahl der Samples
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(audioStreamRef.current!);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      dataArrayRef.current = dataArray;

      const recorder = new MediaRecorder(audioStreamRef.current!, { mimeType });
      mediaRecorderRef.current = recorder;

      setMediaRecorder(recorder);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        let blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        chunkIdRef.current++;
        const isLastChunk = recordingStateRef.current === "inactive";
        if (!isSiteSettingsLoading && logLevel >= 3) console.log('isLastChunk', isLastChunk); // Debugging-Log
        if (!isSiteSettingsLoading && logLevel >= 3) console.log('recordingStateRef.current ', recordingStateRef.current); // Debugging-Log


        sendAudioChunkToServer(blob, fileNameRef.current, sessionIdRef.current, chunkIdRef.current, isLastChunk);
        if (!isSiteSettingsLoading && logLevel >= 3) console.log('RecordingState:', recordingState); // Debugging-Log
        if (!isSiteSettingsLoading && logLevel >= 3) console.log('recordingStateRef.current ', recordingStateRef.current); // Debugging-Log

        if (recordingStateRef.current !== "inactive") {
          startRecordingLoop();
        } else {
          try {
            //triggerUploadCompletion(sessionIdRef.current, fileNameRef.current, audioName, chunkIdRef.current);
          } catch (error) {
            if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim automatischen Abschluss der Verarbeitung:', error);
            alert('Fehler beim automatischen Abschluss der Verarbeitung. Bitte versuchen Sie es manuell.');
          }
        }
      }

      recorder.start(); // Aufnahme starten
      setTimeout(() => {
        if (recorder.state !== "inactive") {
          recorder.stop();
        }
      }, 600000); // Nach x ms stoppen um Chunks zu senden

      setRecordingState('recording');
      setAudioChunks([]);
      // Start monitoring volume level
      startVolumeMonitoring();
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Error starting recording:', error);
      alert('Fehler beim Starten der Aufnahme');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.pause();
      setRecordingState('paused');
      recordingStateRef.current = 'paused';
      if (timerRef.current) {
        clearInterval(timerRef.current); // Timer stoppen
      }
      //alert('Aufnahme pausiert');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'paused') {
      mediaRecorder.resume();
      setRecordingState('recording');
      recordingStateRef.current = 'recording';
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
      //alert('Aufnahme fortgesetzt');
    }
  };

  const stopRecording = async () => {
    if (mediaRecorder) {
      setRecordingState('inactive');
      recordingStateRef.current = 'inactive';

      mediaRecorder.stop();
      stream?.getTracks().forEach((track) => track.stop());
      setStream(null);
      setMediaRecorder(null);

      if (timerRef.current) {
        clearInterval(timerRef.current); // Timer stoppen
        timerRef.current = null;
      }
      setRecordingTime(0);
      setIsSessionSelected(true);
      // Zeit zurücksetzen
      //alert('Aufnahme gestoppt. Verarbeitung wird automatisch gestartet.');

      // Dialogfeld automatisch schließen
      // Trigger automatisch ausführen
    }
  };

  const abortRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    setMediaRecorder(null);
    setRecordingState('inactive');
    recordingStateRef.current = 'inactive';

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setRecordingTime(0);
    setStream(null);
    setAudioChunks([]);
  };



  const startVolumeMonitoring = () => {
    const canvas = volumeBarRef.current;
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    if (!canvas || !analyser || !dataArray) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    const monitor = () => {
      if (recordingStateRef.current === 'paused') {
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.fillRect(0, 0, width, height);

        // Pause-Zeichen (zwei Balken)
        ctx.fillStyle = '#666';
        const barWidth = 10;
        const barGap = 8;
        const barHeight = 30;
        const x = width / 2 - barWidth - barGap / 2;
        const y = height / 2 - barHeight / 2;
        ctx.fillRect(x, y, barWidth, barHeight);
        ctx.fillRect(x + barWidth + barGap, y, barWidth, barHeight);

        return; // abbrechen, wenn pausiert
      }
      analyser.getByteTimeDomainData(dataArray);

      ctx.clearRect(0, 0, width, height);
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#999999'; // Grau

      ctx.beginPath();

      const sliceWidth = width / dataArray.length;
      let x = 0;

      for (let i = 0; i < dataArray.length; i++) {
        const v = dataArray[i] / 128.0;
        const centerIndex = dataArray.length / 2;
        const distanceFromCenter = Math.abs(i - centerIndex) / centerIndex; // 0 in der Mitte, 1 an den Enden
        const amplitude = 8 * (1.2 - distanceFromCenter); // maximale Amplitude in der Mitte
        const y = (v - 1) * (height / 2) * amplitude + height / 2;

        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      ctx.lineTo(width, height / 2);
      ctx.stroke();

      if (recordingState === 'recording') {
        requestAnimationFrame(monitor);
      }
    };

    monitor();
  };

  const handleAbort = () => {
    dialog({
      type: 'confirm',
      title: 'Aufnahme abbrechen',
      message: 'Soll die Aufnahme wirklich gelöscht werden?',
      confirmButton: 'Bestätigen',
      cancelButton: 'Abbrechen',

      onConfirm: () => abortRecording(),
    });
  };





  const sendAudioChunkToServer = async (
    chunk: Blob,
    filename: string,
    session_id: string,
    chunk_id: number,
    is_lastchunk: boolean
  ) => {
    const formData = new FormData();
    formData.append('audio', chunk, filename);
    formData.append('filename', filename);
    formData.append('session_id', session_id);
    formData.append('chunk_id', chunk_id.toString());
    formData.append('is_lastchunk', is_lastchunk.toString());
    formData.append('recording_type', "recording");
    formData.append('transcript_type', "report");
    formData.append('entity_type', isCase ? 'case' : 'session');



    try {
      const response = await axios.post(`/api/services/stream-audio/`, formData);
      formData.forEach((value, key) => console.log(key, value));
      if (!isSiteSettingsLoading && logLevel >= 3) console.log('Audio-Chunk erfolgreich gesendet:', response.data);
      if (response.data.session_DB_id) {
        fetchSessionDataById(response.data.session_DB_id);
        setSelectedSessionId(response.data.session_DB_id);
        setIsSessionSelected(true);

      }


    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim Senden eines Audio-Chunks:', error);
    }
  };






  const isRecording = recordingState !== 'inactive';



  return (
    <div className="tw-text-gray-500 tw-flex-1">
      {isRecording && (
        <div className="fixed inset-0 bg-black bg-opacity-15 z-2" />
      )}
      {isRecording && (
        <Portal>
          <div
            id="header-overlay"
            className="fixed top-0 left-0 right-0  bg-black bg-opacity-15 z-150"
            style={{
              height: `${document.getElementById('header')?.getBoundingClientRect().height ?? 65}px`
            }}
          />
        </Portal>

      )}

      <div style={{ display: 'grid', gridTemplateRows: '1fr auto', height: '100%' }}>
        <div data-state="active" data-orientation="horizontal" role="tabpanel" aria-labelledby="radix-113-trigger-recording" id="radix-113-content-recording" tabIndex={0}>
          <div className="tw-flex tw-h-full tw-flex-col">
            <div className="tw-mb-1 tw-gap-1 tw-flex tw-flex-row tw-justify-center">
              {isCase ? "Für Berichte" : "Für Sitzungen, die nicht aufgenommen wurden"}
              <div className="lg:tw-hidden">
                {/*<div><img src="Record%20session_files/info-circle-a5f42b35.svg" alt="info icon" data-state="closed" /></div>*/}
              </div>
            </div>
            <div className="tw-flex-1 tw-flex-unset tw-flex tw-flex-col tw-justify-center">
              {recordingState === 'inactive' ? (
                <div className="recorder__start-wrapper tw-flex tw-flex-col tw-items-center">
                  <div className="tw-grid">
                    <button
                      onClick={startRecording}
                      className={`_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !tw-font-normal${isCase ? " !bg-[#E7A555]" : ""} `}
                    >
                      <div className="_btnFlex_19rto_32">
                        <div className="_btnAddon_19rto_56 _btnLeftAddon_19rto_61">
                          <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="tw-w-5">
                            <path d="M16.513 20.6641C19.4597 20.6641 21.8464 18.2774 21.8464 15.3307V7.9974C21.8464 5.05073 19.4597 2.66406 16.513 2.66406C13.5664 2.66406 11.1797 5.05073 11.1797 7.9974V15.3307C11.1797 18.2774 13.5664 20.6641 16.513 20.6641Z" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"></path><path d="M6.32031 12.8672V15.1339C6.32031 20.7605 10.8936 25.3339 16.5203 25.3339C22.147 25.3339 26.7203 20.7605 26.7203 15.1339V12.8672" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"></path><path d="M14.6641 8.57219C15.8641 8.13219 17.1707 8.13219 18.3707 8.57219" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"></path><path d="M15.4531 11.3978C16.1598 11.2111 16.8931 11.2111 17.5998 11.3978" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"></path><path d="M16.5156 25.3359V29.3359" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"></path>

                          </svg>
                        </div>
                        Diktat starten
                      </div>
                    </button>
                  </div>
                  <div className="tw-flex tw-justify-center tw-items-center tw-gap-2 tw-mt-2">
                    <img className="tw-w-4" src="/dsgvo.png" alt="" />
                    <span>100% DSGVO konform</span><div>
                      <button className="tw-flex" data-state="closed">
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8.00033 1.32487C11.9537 1.32487 15.167 4.5382 15.167 8.49154C15.167 12.4449 11.9537 15.6582 8.00033 15.6582C4.04699 15.6582 0.833658 12.4449 0.833658 8.49154C0.833658 4.5382 4.04699 1.32487 8.00033 1.32487ZM8.00033 14.6582C11.4003 14.6582 14.167 11.8915 14.167 8.49154C14.167 5.09154 11.4003 2.32487 8.00033 2.32487C4.60033 2.32487 1.83366 5.09154 1.83366 8.49154C1.83366 11.8915 4.60033 14.6582 8.00033 14.6582Z" fill="#8F8F8F"></path>
                          <path d="M8 7.32487C8.27333 7.32487 8.5 7.55154 8.5 7.82487V11.1582C8.5 11.4315 8.27333 11.6582 8 11.6582C7.72667 11.6582 7.5 11.4315 7.5 11.1582V7.82487C7.5 7.55154 7.72667 7.32487 8 7.32487Z" fill="#8F8F8F"></path>
                          <path d="M8.00033 5.15828C8.08699 5.15828 8.17366 5.17828 8.25366 5.21161C8.33366 5.24495 8.40699 5.29162 8.47366 5.35162C8.53366 5.41828 8.58033 5.48495 8.61366 5.57162C8.64699 5.65162 8.66699 5.73828 8.66699 5.82495C8.66699 5.91161 8.64699 5.99828 8.61366 6.07828C8.58033 6.15828 8.53366 6.23161 8.47366 6.29828C8.40699 6.35828 8.33366 6.40495 8.25366 6.43828C8.09366 6.50495 7.90699 6.50495 7.74699 6.43828C7.66699 6.40495 7.59366 6.35828 7.52699 6.29828C7.46699 6.23161 7.42033 6.15828 7.38699 6.07828C7.35366 5.99828 7.33366 5.91161 7.33366 5.82495C7.33366 5.73828 7.35366 5.65162 7.38699 5.57162C7.42033 5.48495 7.46699 5.41828 7.52699 5.35162C7.59366 5.29162 7.66699 5.24495 7.74699 5.21161C7.82699 5.17828 7.91366 5.15828 8.00033 5.15828Z" fill="#8F8F8F"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="tw-max-w-[600px] tw-mx-auto tw-relative">
                  <svg onClick={() => handleAbort()} className="tw-absolute tw-right-0 tw-top-0 tw-cursor-pointer tw-bg-white tw-fill-white tw-w-[20px] tw-h-[20px]" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8.00004 14.6666C11.6667 14.6666 14.6667 11.6666 14.6667 7.99992C14.6667 4.33325 11.6667 1.33325 8.00004 1.33325C4.33337 1.33325 1.33337 4.33325 1.33337 7.99992C1.33337 11.6666 4.33337 14.6666 8.00004 14.6666Z" stroke="#32768D" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M6.11328 9.88661L9.88661 6.11328" stroke="#32768D" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9.88661 9.88661L6.11328 6.11328" stroke="#32768D" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="_visualizer_o5yw5_1">
                    <div className="_canvasWrapper_o5yw5_28">
                      <canvas ref={el => el && (volumeBarRef.current = el)} height="70" style={{ width: '100%', height: '100%' }} width="419"></canvas>
                    </div>
                    <div className="_secondsWrapper_o5yw5_34">{formatTime(recordingTime)}</div>
                  </div>
                  <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-2 tw-mb-2">
                    <div className="tw-flex-1 bg-white rounded-[5px]">
                      <button onClick={() => handleAbort()} className="_button_19rto_40 _secondary_19rto_163 _filled_19rto_80 _block_19rto_52 recorder__delete-btn">
                        <div className="_btnFlex_19rto_32">Löschen</div>
                      </button>
                    </div>
                    <div className="tw-flex-1 bg-white rounded-[5px]">
                      {recordingState === 'recording' ? (
                        <button onClick={pauseRecording} className="_button_19rto_40 _primary_19rto_127 _outlined_19rto_69 _block_19rto_52 recorder__pause-btn !tw-font-normal">
                          <div className="_btnFlex_19rto_32">Pause</div>
                        </button>
                      ) : (
                        <button onClick={resumeRecording} className="_button_19rto_40 _primary_19rto_127 _outlined_19rto_69 _block_19rto_52 recorder__pause-btn !tw-font-normal">
                          <div className="_btnFlex_19rto_32">Weiter</div>
                        </button>
                      )}
                    </div>
                    <div className="tw-flex-1">
                      <button onClick={stopRecording}   className={`_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !tw-font-normal${isCase ? " !bg-[#E7A555]" : ""} `}
>
                        <div className="_btnFlex_19rto_32">Diktat beenden</div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center lg:tw-mt-4 tw-mt-1">
              <div className="tw-flex tw-gap-2 tw-items-center">Aufnahmedauer: 1–15 min</div>
              {recordingState !== 'inactive' && (
                <p className="tw-text-[red] tw-m-0">Aktuelle Aufnahme: {Math.floor(recordingTime / 60)}min {recordingTime % 60}sec</p>
              )}
            </div>
          </div>
        </div>
        <div className="tab-tip tw-mt-1">
          <>
            <button
              className="btn-reset tw-underline tw-text-primary-500"
              onClick={() => setShowTipsDialog(true)}
            >
              Tipps zum Diktat
            </button>

            {showTipsDialog && (
              <Portal>

                <ModalWrapper onClose={() => setShowTipsDialog(false)} title="Tipps zum Diktat">
                  <div className="tw-p-4">
                    <ul className="sc-gEvEer fteAEG tw-grid tw-gap-4">
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <AssignmentTurnedInIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Für das Diktieren ist keine Einwilligung der Klient:innen erforderlich, wenn Sie keine personenbezogenen Daten benennen. Falls Sie eine wünschen, <Link to="/settings/clientconsent" className="sc-eqUAAy iEhnxW underline">laden Sie hier eine Vorlage herunter.</Link>
                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <MicIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div><span className="tw-font-bold">Für ein optimales Ergebnis vermeiden Sie möglichst Störgeräusche, Pausen und Nebengespräche und achten Sie auf gleichmäßige Lautstärke sowie gut verständliche Stimmen.</span></div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <MusicNoteIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Führen Sie eine Testsitzung durch, um sicherzustellen, dass Ihr Setup stabil ist. {/* <a target="_blank" rel="noreferrer" href="" className="sc-eqUAAy iEhnxW">Mehr erfahren</a> */}
                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <ChatBubbleOutlineIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Eine präzise Benennung von Interventionen und Fachbegriffen hilft LumaNote, die Qualität der Dokumentation zu verbessern.
                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <LabelIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Wenn Sie beim Diktieren die einzelnen Gliederungspunkte der Dokumentationsvorlage ansprechen, kann LumaNote die Inhalte später besser zuordnen und strukturieren. {/* <a target="_blank" rel="noreferrer" href="" className="sc-eqUAAy iEhnxW">Mehr erfahren</a> */}
                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <PersonAddAltIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Das Diktat dient als Zusammenfassung Ihrer Beobachtungen und Gedanken als Therapeut:in.

                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <PlayCircleOutlineIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Bei Pausen wird Ihr Fortschritt gespeichert. Klicken Sie einfach auf „Weiter“.
                          </div>
                        </div>
                      </li>

                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <InfoOutlinedIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Schließen Sie das Diktat über „Diktat beenden“ ab.
                          </div>
                        </div>
                      </li>

                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <ContentCutIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Erstellen Sie bitte für jede Sitzung eine separate Aufnahme.

                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <DeleteForeverIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Die Diktate werden am Ende des selben Tages automatisch gelöscht.  <a target="_blank" rel="noreferrer" href="https://lumanote.de/security" className="sc-eqUAAy iEhnxW tw-text-primary-500 underline">Mehr erfahren</a>

                          </div>
                        </div>
                      </li>
                      {/*<li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <ArticleIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Die Art der Klient:innen und Anzahl der Sprecher:innen beeinflussen die Vorlagenwahl. <a target="_blank" rel="noreferrer" href="" className="sc-eqUAAy iEhnxW">Mehr erfahren</a>
                          </div>
                        </div>
                      </li>*/}
                    </ul>
                  </div>
                </ModalWrapper>
              </Portal>

            )}
          </>
        </div>
      </div>
    </div>

  );


}

export default DictateTab;